import { useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

import { SearchTicketQuery, TicketDto, TicketsClient } from "../web-api-client";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useToast } from "../hooks/use-toast";
import { AnimatePresence } from "framer-motion";
import { SearchTicketForm } from "../components/ticket/SearchSection";
import { SearchTicketResult } from "../components/ticket/ResultSection";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMobile } from "../hooks/use-mobile";
import { useSessionStorageState } from "../hooks/useSessionStorageState";
import { useCountdownTo } from "../hooks/useCountdownTo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { toUserMessage } from "../utils/toUserMessage";
import MobileHeader from "../components/MobileHeader";
import { appConfig } from "../appconfig";

interface FormValues {
  dateFrom: string;
  phone: string;
  dateTo: string;
  captcha: string;
}

export const TicketSearch = () => {
  const captchaScope = "searchTicket";
  const { t } = useLanguage();

  const [captchaUrl, setCaptchaUrl] = useState<string>(
    `${appConfig.apiBaseUrl}/api/captcha/${captchaScope}`,
  );

  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // const [view, setView] = useState<"form" | "result">("form");
  const [savedFormValues, setSavedFormValues] = useState<
    FormValues | undefined
  >(undefined);
  const [responseApi, setResponseApi] = useSessionStorageState(
    "searchResults",
    null
  );

  const { toast } = useToast();
  const navigate = useNavigate();
  const [isMobile] = useIsMobile();

  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") ?? "form";

  const handleSearch = async (formData: FormValues) => {
    setErrorMessage(null);
    setIsSearching(true);
    setSavedFormValues(formData);

    let phone = formData.phone;
    let captcha = formData.captcha;
    let dateFrom = formData.dateFrom;
    let dateTo = formData.dateTo;

    const client = new TicketsClient(appConfig.apiBaseUrl, {
      fetch: fetchWithTimeout(),
    });

    const query = new SearchTicketQuery({
      dateFrom,
      dateTo,
      phone,
      captcha,
      captchaScope,
    });

    try {
      var response = await client.searchTicket(query);

      setResponseApi(response);

      var data = response.items ?? [];
      if (data.length === 0) {
        setErrorMessage(t("khongtimthaylichhen"));
      }
    } catch (error: any) {
      const errorMsg = await toUserMessage(error);

      toast({
        variant: "destructive",
        title: t("thatbai"),
        description: errorMsg,
        duration: 3000,
      });

      setErrorMessage(errorMsg);
    } finally {
      setIsSearching(false);
      reloadCaptcha();
    }
  };

  const onExpire = () => {
    setResponseApi(null);
    // navigate("/ticket/expired", { replace: true });
  };

  const { ready, expired } = useCountdownTo(responseApi?.expiresAt, onExpire);

  const onBack = () => {
    navigate(-1);
    setResponseApi(null);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("view");
      return newParams;
    });
  };

  const onViewDetail = (ticket: TicketDto) => {
    navigate(`/phieu-kham-benh/${ticket.ticketId}`, {
      state: {
        phone: ticket.phoneNumber,
        dateBooking: ticket.appointmentDate,
        view: "detail",
      },
    });
  };

  const reloadCaptcha = () => {
    setCaptchaUrl(`${appConfig.apiBaseUrl}/api/captcha/${captchaScope}?${new Date().getTime()}`);
  };

  const gotoResultView = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("view", "result");
      return newParams;
    });
  };

  useEffect(() => {
    reloadCaptcha();
  }, []);

  useEffect(() => {
    document.title = t("tracuuphieukham");
  }, [t]);

  useEffect(() => {
    var data = responseApi?.items ?? [];
    if (view === "result" && data.length === 0) {
      navigate("/phieu-kham");
    }
  }, [searchParams]);

  useEffect(() => {
    var data = responseApi?.items ?? [];

    if (data.length > 0) {
      gotoResultView();
    }
  }, [responseApi]);

  return (
    <>
      {isMobile && view === "result" && <MobileHeader title={t("ketquatracuu")} onBack={onBack} />}
      <AnimatePresence mode="wait">
        {view === "form" && (
          <SearchTicketForm
            onSubmit={handleSearch}
            errorMessage={errorMessage}
            isLoading={isSearching}
            captchaUrl={captchaUrl}
            reloadCaptcha={reloadCaptcha}
            defaultForm={savedFormValues}
            isMobile={isMobile}
          />
        )}
        {view === "result" && (
          <div>
            {expired && ready && (
              <Dialog open>
                <DialogContent
                  showCloseButton={false}
                  className="p-4 w-[400px] rounded-xl overflow-hidden shadow-2xl"
                >
                  {/* tránh warning */}
                  <DialogHeader>
                    <DialogTitle>{t("phientracuuhethan")}</DialogTitle>
                    <DialogDescription>
                      {t("phientracuuhethanmota")}
                    </DialogDescription>
                  </DialogHeader>

                  <DialogFooter className="flex justify-center">
                    <Button onClick={() => window.location.reload()}>
                      {t("tailai")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {responseApi &&
              responseApi.items &&
              responseApi.items.length > 0 && (
                <SearchTicketResult
                  data={responseApi.items}
                  onBack={onBack}
                  onViewDetail={onViewDetail}
                  isMobile={isMobile}
                />
              )}
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
